/**
 * Data layer for `advertiser-campaign-upload`. Thin typed fetch wrappers over
 * /api/v1/advertiser-campaign-upload. Raw `fetch()` is confined to this file
 * per the dash contract (D6).
 *
 * Backend reference: apps/api/src/routes/advertiser-campaign-upload.ts.
 * Endpoints:
 *   POST   /submit
 *   GET    /list/:advertiser_id
 *   POST   /approve/:id        (admin; x-reviewer-id header)
 *   POST   /reject/:id         (admin; body.admin_notes required)
 *   GET    /admin-queue
 */

export type PayoutType = "cpa" | "cpl" | "cps" | "cpc" | "revshare" | "hybrid";
export type UploadStatus = "draft" | "submitted" | "approved" | "rejected";

export const PAYOUT_TYPES: readonly PayoutType[] = [
	"cpa",
	"cpl",
	"cps",
	"cpc",
	"revshare",
	"hybrid",
] as const;

export interface CampaignUploadRow {
	id: string;
	advertiser_id: string;
	submitted_by: string;
	name: string;
	destination_url: string;
	payout_type: PayoutType;
	payout_amount: number;
	countries: string;
	status: UploadStatus;
	admin_notes: string | null;
	submitted_at: number | null;
	created_at: number;
	updated_at: number;
	reviewed_at: number | null;
	reviewer_id: string | null;
}

export interface ListResponse {
	items: CampaignUploadRow[];
}

export interface SubmitBody {
	advertiser_id: string;
	submitted_by: string;
	name: string;
	destination_url: string;
	payout_type: PayoutType;
	payout_amount: number;
	countries?: string;
	status?: "draft" | "submitted";
}

export interface RejectBody {
	admin_notes: string;
}

const BASE = "/api/v1/advertiser-campaign-upload";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function submit(body: SubmitBody): Promise<CampaignUploadRow> {
	const res = await fetch(`${BASE}/submit`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CampaignUploadRow>(res);
}

export async function listByAdvertiser(
	advertiserId: string,
): Promise<ListResponse> {
	const res = await fetch(
		`${BASE}/list/${encodeURIComponent(advertiserId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListResponse>(res);
}

export async function adminQueue(): Promise<ListResponse> {
	const res = await fetch(`${BASE}/admin-queue`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListResponse>(res);
}

export async function approve(
	id: string,
	reviewerId?: string,
): Promise<CampaignUploadRow> {
	const headers: Record<string, string> = { accept: "application/json" };
	if (reviewerId && reviewerId.trim().length > 0) {
		headers["x-reviewer-id"] = reviewerId.trim();
	}
	const res = await fetch(
		`${BASE}/approve/${encodeURIComponent(id)}`,
		{
			method: "POST",
			credentials: "include",
			headers,
		},
	);
	return parseOrThrow<CampaignUploadRow>(res);
}

export async function reject(
	id: string,
	body: RejectBody,
	reviewerId?: string,
): Promise<CampaignUploadRow> {
	const headers: Record<string, string> = {
		accept: "application/json",
		"content-type": "application/json",
	};
	if (reviewerId && reviewerId.trim().length > 0) {
		headers["x-reviewer-id"] = reviewerId.trim();
	}
	const res = await fetch(
		`${BASE}/reject/${encodeURIComponent(id)}`,
		{
			method: "POST",
			credentials: "include",
			headers,
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<CampaignUploadRow>(res);
}

export const advertiserCampaignUploadQueryKeys = {
	all: ["advertiser-campaign-upload"] as const,
	list: (advertiserId: string) =>
		[...advertiserCampaignUploadQueryKeys.all, "list", advertiserId] as const,
	adminQueue: () =>
		[...advertiserCampaignUploadQueryKeys.all, "admin-queue"] as const,
	detail: (id: string) =>
		[...advertiserCampaignUploadQueryKeys.all, id] as const,
};

export function formatCents(cents: number | null | undefined): string {
	if (cents === null || cents === undefined || !Number.isFinite(cents)) {
		return "-";
	}
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString()}.${rem.toString().padStart(2, "0")}`;
}

export function formatUnixDate(ts: number | null | undefined): string {
	if (!ts) return "-";
	const d = new Date(ts);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toLocaleDateString();
}
